import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [canCreateProperty, setCanCreateProperty] = useState(false);
  const [hover, setHover] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [loadingUser, setLoadingUser] = useState(true);

  const [pinned, setPinned] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sidebarPinned")) ?? false;
    } catch {
      return false;
    }
  });

  const isMobile = screenWidth <= 700;
  const isTablet = screenWidth > 700 && screenWidth <= 1050;

  const expanded = isMobile ? false : hover || pinned;
  const active = location.pathname;

  useEffect(() => {
    carregarTudo();

    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarPinned", JSON.stringify(pinned));
  }, [pinned]);

  useEffect(() => {
    carregarUsuario(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const carregarTudo = async () => {
    await carregarUsuario(true);
    await carregarImoveis();
    await carregarPermissaoCriar();
  };

  const carregarUsuario = async (comRetry = false) => {
    try {
      setLoadingUser(true);

      const res = await axios.get("/auth/me", {
        withCredentials: true
      });

      setUser(res.data);
    } catch (err) {
      console.log("Erro ao carregar usuário na sidebar:", err);

      if (err?.response?.status === 401) {
        setUser(null);
        navigate("/");
        return;
      }

      if (comRetry) {
        setTimeout(async () => {
          try {
            const retry = await axios.get("/auth/me", {
              withCredentials: true
            });

            setUser(retry.data);
          } catch (retryErr) {
            console.log("Retry sidebar falhou:", retryErr);
          }
        }, 1200);
      }
    }

    setLoadingUser(false);
  };

  const carregarImoveis = async () => {
    try {
      const res = await axios.get("/properties/list", {
        withCredentials: true
      });

      setProperties(res.data || []);
    } catch (err) {
      console.log("Erro ao carregar imóveis na sidebar:", err);
      setProperties([]);
    }
  };

  const carregarPermissaoCriar = async () => {
    try {
      const res = await axios.get(
        "/properties/permissions/create",
        {
          withCredentials: true
        }
      );

      setCanCreateProperty(res.data.canCreateProperty === true);
    } catch (err) {
      console.log("Erro ao carregar permissão de criar imóvel:", err);
      setCanCreateProperty(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        "/auth/logout",
        {},
        {
          withCredentials: true
        }
      );
    } catch {
      document.cookie =
        "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    window.location.href = "/";
  };

  const go = (path) => {
    navigate(path);
  };

  const userPhoto =
    user?.foto ||
    user?.picture ||
    user?.photo ||
    user?.avatar ||
    fallback;

  const sidebarName =
    user?.apelido ||
    user?.nome ||
    "Usuário";

  if (isMobile) {
    return (
      <div style={mobileStyles.wrapper}>
        <div
          style={{
            ...mobileStyles.bar,
            gridTemplateColumns: canCreateProperty
              ? "repeat(5, 1fr)"
              : "repeat(4, 1fr)"
          }}
        >
          <button
            onClick={() => go("/dashboard")}
            style={{
              ...mobileStyles.item,
              color: active === "/dashboard" ? "#60a5fa" : "#94a3b8"
            }}
          >
            <span style={mobileStyles.icon}>🏠</span>
            <span style={mobileStyles.text}>Home</span>
          </button>

          {canCreateProperty && (
            <button
              onClick={() => go("/criar-imovel")}
              style={{
                ...mobileStyles.item,
                color: active === "/criar-imovel" ? "#60a5fa" : "#94a3b8"
              }}
            >
              <span style={mobileStyles.icon}>➕</span>
              <span style={mobileStyles.text}>Criar</span>
            </button>
          )}

          <button
            onClick={() => {
              if (properties.length > 0) {
                go(`/imovel/${properties[0].id}`);
              }
            }}
            style={{
              ...mobileStyles.item,
              color: active.startsWith("/imovel") ? "#60a5fa" : "#94a3b8"
            }}
          >
            <span style={mobileStyles.icon}>🏘️</span>
            <span style={mobileStyles.text}>Imóveis</span>
          </button>

          <button
            onClick={() => go("/imoveis-vendidos")}
            style={{
              ...mobileStyles.item,
              color: active === "/imoveis-vendidos" ? "#60a5fa" : "#94a3b8"
            }}
          >
            <span style={mobileStyles.icon}>✅</span>
            <span style={mobileStyles.text}>Vendidos</span>
          </button>

          <button
            onClick={() => go("/perfil")}
            style={{
              ...mobileStyles.item,
              color: active === "/perfil" ? "#60a5fa" : "#94a3b8"
            }}
          >
            <img
              src={userPhoto}
              alt={sidebarName}
              style={mobileStyles.avatar}
            />

            <span style={mobileStyles.text}>Perfil</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <aside
      style={{
        ...styles.sidebar,
        width: expanded ? (isTablet ? 220 : 250) : isTablet ? 64 : 72
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {expanded && (
        <button
          onClick={() => setPinned(!pinned)}
          style={{
            ...styles.pinBtn,
            background: pinned ? "#60a5fa" : "rgba(255,255,255,0.25)"
          }}
          title={pinned ? "Desfixar sidebar" : "Fixar sidebar"}
        />
      )}

      <div style={styles.top}>
        <img
          src="/HITC FUNDO TRANSPARENTE.png"
          alt="HITC"
          style={expanded ? styles.logoOpen : styles.logoClosed}
        />
      </div>

      <nav
        style={{
          ...styles.menu,
          marginTop: expanded ? 58 : 72
        }}
      >
        <SidebarItem
          icon="🏠"
          label="Dashboard"
          active={active === "/dashboard"}
          expanded={expanded}
          onClick={() => go("/dashboard")}
        />

        {canCreateProperty && (
          <SidebarItem
            icon="➕"
            label="Criar Imóvel"
            active={active === "/criar-imovel"}
            expanded={expanded}
            onClick={() => go("/criar-imovel")}
          />
        )}

        <SidebarItem
          icon="✅"
          label="Imóveis Vendidos"
          active={active === "/imoveis-vendidos"}
          expanded={expanded}
          onClick={() => go("/imoveis-vendidos")}
        />

        {expanded && (
          <div style={styles.sectionTitle}>
            Imóveis ativos
          </div>
        )}

        {properties.map((property) => (
          <SidebarItem
            key={property.id}
            icon="🏘️"
            label={property.nome || `Imóvel ${property.id}`}
            active={active === `/imovel/${property.id}`}
            expanded={expanded}
            onClick={() => go(`/imovel/${property.id}`)}
          />
        ))}
      </nav>

      <div style={styles.bottom}>
        <img
          src={userPhoto}
          alt={sidebarName}
          style={styles.avatar}
        />

        {expanded && (
          <>
            <div style={styles.userInfo}>
              <div style={styles.name}>
                {loadingUser && !user ? "Carregando..." : sidebarName}
              </div>
            </div>

            <div style={styles.actions}>
              <button
                onClick={() => go("/perfil")}
                style={styles.iconBtn}
                title="Perfil"
              >
                ⚙️
              </button>

              <button
                onClick={logout}
                style={styles.logoutBtn}
              >
                Sair
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function SidebarItem({
  icon,
  label,
  active,
  expanded,
  onClick
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...styles.item,
        justifyContent: expanded ? "flex-start" : "center",
        background: active ? "rgba(37,99,235,0.18)" : "transparent",
        border: active
          ? "1px solid rgba(96,165,250,0.35)"
          : "1px solid transparent",
        color: active ? "#dbeafe" : "#94a3b8"
      }}
      title={!expanded ? label : ""}
    >
      <span style={styles.icon}>
        {icon}
      </span>

      {expanded && (
        <span style={styles.label}>
          {label}
        </span>
      )}
    </button>
  );
}

const styles = {
  sidebar: {
    minHeight: "100vh",
    height: "100vh",
    background: "linear-gradient(180deg, #071022, #020617)",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    color: "#e5e7eb",
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    left: 0,
    overflow: "hidden",
    transition: "width 0.25s ease",
    flexShrink: 0,
    zIndex: 20
  },

  pinBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    zIndex: 10
  },

  top: {
    height: 96,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },

  logoOpen: {
    width: 94,
    transition: "0.25s ease"
  },

  logoClosed: {
    width: 38,
    opacity: 0.9,
    transition: "0.25s ease"
  },

  menu: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "0 10px",
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1
  },

  item: {
    width: "100%",
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 12px",
    borderRadius: 12,
    background: "transparent",
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
    transition: "0.18s ease",
    fontSize: 14
  },

  icon: {
    fontSize: 18,
    width: 22,
    minWidth: 22,
    textAlign: "center"
  },

  label: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  sectionTitle: {
    color: "#64748b",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginTop: 14,
    marginBottom: 4,
    paddingLeft: 10
  },

  bottom: {
    minHeight: 66,
    padding: 10,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0
  },

  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0
  },

  userInfo: {
    minWidth: 0,
    flex: 1
  },

  name: {
    color: "#f8fafc",
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: 6
  },

  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 9,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  logoutBtn: {
    height: 32,
    padding: "0 10px",
    borderRadius: 9,
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.3)",
    color: "#ef4444",
    fontSize: 12,
    cursor: "pointer"
  }
};

const mobileStyles = {
  wrapper: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    height: 72,
    background: "rgba(2,6,23,0.92)",
    backdropFilter: "blur(14px)",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    zIndex: 100
  },

  bar: {
    height: "100%",
    display: "grid",
    alignItems: "center"
  },

  item: {
    height: "100%",
    border: "none",
    background: "transparent",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    cursor: "pointer"
  },

  icon: {
    fontSize: 20
  },

  text: {
    fontSize: 10
  },

  avatar: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    objectFit: "cover"
  }
};